import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import classNames from "classnames";
import { Check as CheckIcon, Plus as PlusIcon, X as XIcon } from "lucide-react";
import * as React from "react";

import { extendSelection } from "@/shared/array";
import { NOT_SET_LABEL } from "@/shared/domain/query/notSet";
import {
  Button,
  Spinner,
  TextInput,
  type TextInputSize,
} from "@/shared/foundation/primitives";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";

import {
  type FilterOption,
  type FilterOptions,
  type FilterOptionsResolver,
  type LabeledOption,
  type ResolvedOptions,
} from "../FilterDefinition";
import {
  type EnumComparator,
  type SetComparator,
  isValueless,
} from "../FilterSchema";
import type { ListMemberLabels } from "../operators";

import { ComparatorPill, ListMember } from "./ListMember";
import styles from "./OptionsValueInput.module.css";

// Trailing debounce applied to the search input before a server-searched
// resolver is re-invoked, so a burst of typing collapses to one query. Matches
// the convention used by other server-search inputs (e.g. `OrgAutocomplete`).
const SEARCH_DEBOUNCE_MS = 300;

// Default shortest search term worth sending to a server resolver. Postgres
// can extract no trigram from a 1-2 character `%term%`, so those searches
// degenerate to a full index scan — slower than having no index at all. The
// empty search stays allowed: it is the intentional first-page-on-open query.
// A source that searches short terms cheaply lowers it (`minSearchLength`).
const MIN_SERVER_SEARCH_LENGTH = 3;

// Fixed height (px) of one option row. Rows are single-line — the label
// truncates with an ellipsis (see `.row-label`) — so a constant estimate is
// exact and the virtualizer never needs to re-measure. This constant is the
// single source of the row height: each row's inline `height` style derives
// from it (the stylesheet declares none).
const OPTION_ROW_HEIGHT = 32;

// The button beside the search box reads one or the other: Clear all while
// anything is ticked, else Select all.
const SELECTION_ACTION_LABELS = ["Clear all", "Select all"] as const;

// Rows rendered outside the viewport on each side, trading a little extra DOM
// for scroll headroom so fast scrolling doesn't flash blank rows.
const OPTION_OVERSCAN = 8;

// The fewest rows the list shrinks to where a parent caps its height (the
// filter popover's form, low in a short window), so there is still a choice
// in view. A shorter list is just as tall as its rows.
const MIN_VISIBLE_ROWS = 3;

interface OptionsValueInputBaseProps {
  comparator: SetComparator | EnumComparator;
  /**
   * The selected options — wire ``value`` plus the display ``label`` the user
   * saw when picking each one. Carrying the label in the selection is what
   * lets chips and pinned rows render labels for server-searched options
   * whose full option list isn't available later.
   */
  value: LabeledOption[];
  /**
   * Fired on every selection change with the next selected options. A change
   * that also adds or removes "Not set" (Clear all, Select all) passes its
   * next state as `notSet`, in the same call, rather than calling
   * `notSet.onToggle` too: a parent that rebuilds its filter from each call
   * would build the second from the filter before the first, undoing it.
   */
  onChange: (next: LabeledOption[], notSet?: boolean) => void;
  /** Field size, on the `TextInput` scale. @default "md" */
  size?: TextInputSize;
  /** Marks the search box invalid, for an error shown beside it. */
  invalid?: boolean;
  /**
   * The "Not set" member of the selection, listed as a row ahead of the
   * options while given — unless the source reports every entity set
   * (`hasUnset: false`) and it isn't selected. It is a flag of the filter
   * rather than an option, so it has its own state and handler and can never
   * collide with an option whose label reads the same. Omit it where the
   * field can't be unset or the comparator can't hold it.
   */
  notSet?: NotSetRow;
  /**
   * Draw each row as a member of an "is any of" / "is none of" list: its
   * comparator pill before each option ("is Failed"), and as the whole of the
   * "Not set" row ("is not set"). Omit it where the comparator is not a list
   * of equalities (a set's CONTAINS, an identity's principal type); rows are
   * then the bare labels.
   */
  memberLabels?: ListMemberLabels;
  /**
   * Whether some entity in scope leaves the field unset, for a static
   * `options` array; a resolver reports it per page instead
   * (`ResolvedOptions.hasUnset`). `false` withholds the "Not set" row unless
   * it is already selected; leave it out when the source can't tell.
   */
  hasUnset?: boolean;
  /**
   * Each option's popularity (how many entities hold it), keyed by option
   * value, for a static `options` array; a resolver reports it per page
   * instead (`ResolvedOptions.counts`). Shown on each row, and the order of
   * the selected values (see `OptionList`).
   */
  counts?: Readonly<Record<string, number>>;
  /** How many entities leave the field unset, shown on the "Not set" row. */
  unsetCount?: number;
  /**
   * Shortest search sent to a resolver; shorter ones keep the previous page.
   * @default 3 (see `MIN_SERVER_SEARCH_LENGTH`)
   */
  minSearchLength?: number;
  /** Focus the search box on mount. @default false */
  autoFocus?: boolean;
  /**
   * Let the author add values the source doesn't list, for a field whose
   * options are hints rather than a closed set (see {@link CustomValueOptions}).
   */
  customValue?: CustomValueOptions;
  /**
   * Called with whether the typed text could be added as a value, so a form
   * can accept a value that is typed but not yet added (it is added when focus
   * leaves the picker). Only reported with `customValue`.
   */
  onDraftValidChange?: (valid: boolean) => void;
}

/**
 * How the picker takes typed values. With it, the typed text is offered as a
 * "Use “…”" row, always the last row, and Enter in the search box adds it (or
 * ticks the listed option it names), as does focus leaving the picker.
 */
export interface CustomValueOptions {
  /**
   * The value `text` stands for, written as the field stores it, or
   * `undefined` when the field can't hold it. Typed text is compared and added
   * in this form, so "3.0" and "3" name one number. `text` is as typed, spaces
   * and all: a string's spaces make it a different value, so a field whose
   * values ignore them (a number) trims them itself.
   */
  normalize: (text: string) => string | undefined;
}

/** The "Not set" row's state and its toggle. */
export interface NotSetRow {
  /** Whether "Not set" is part of the selection. */
  selected: boolean;
  /** Called when the row is pressed; the caller flips `selected`. */
  onToggle: () => void;
}

/**
 * ``options`` in either form, with ``optionsKey`` REQUIRED alongside a
 * resolver and forbidden alongside a static array.
 *
 * A resolver's results are fetched, so the query needs a name for WHICH source
 * produced them. The picker cannot derive that itself: a caller may rebuild an
 * equivalent resolver on every render (an inline arrow, say), so the function
 * reference is worthless as an identity. The caller names the source instead,
 * and the type system makes forgetting to a compile error.
 *
 * Callers switching between genuinely different sources should ALSO remount the
 * picker (``key``) so its search text and latched option set reset — see the
 * identity branch of ``FilterForm``. ``optionsKey`` then keeps the two sources'
 * query entries distinct across that remount.
 *
 * A static array needs none of this — it is re-derived from props on every
 * render — so passing a key there is rejected rather than quietly ignored.
 */
type OptionsSourceProps =
  | {
      /** Statically known to be an in-memory array, so no key is needed. */
      options: string[] | LabeledOption[];
      optionsKey?: never;
    }
  | {
      /**
       * Possibly (or definitely) a resolver. Callers holding an un-narrowed
       * ``FilterOptions`` land here — which is why this branch accepts arrays
       * too, rather than forcing every generic call site to narrow before it
       * can render. A key supplied for what turns out to be a static array is
       * simply unused.
       */
      options: FilterOptions;
      /**
       * Names WHICH option source ``options`` currently is, for the query key.
       * Change it when the resolver starts representing something else (a
       * different principal kind, a different field); keep it fixed when the
       * same source is merely rebuilt. A local discriminator like the principal
       * kind or the field name is sufficient.
       */
      optionsKey: string;
    };

type OptionsValueInputProps = OptionsValueInputBaseProps & OptionsSourceProps;

function normalizeOption(option: FilterOption): LabeledOption {
  return typeof option === "string" ? { label: option, value: option } : option;
}

/**
 * Searchable, multi-select checkbox list shared by the ``set``, ``enum`` and
 * ``identity`` filter types, with an optional "Not set" row (``notSet``).
 * Valueless comparators (``IS_NULL`` / ``IS_NOT_NULL``) render nothing —
 * redundant with ``FilterForm``, which already hides the value row for them.
 *
 * ``options`` drives one of two modes:
 *
 * - **Static array** (``string[]`` or ``LabeledOption[]``): the whole list is
 *   held in memory and filtered CLIENT-SIDE by label substring as the user
 *   types. No async, no debounce.
 * - **Server-searched resolver** (function form, see
 *   {@link FilterOptionsResolver}): the resolver is invoked with the empty
 *   search on open, and that first page decides what happens next. If it was
 *   complete, the picker keeps it and filters it client-side like a static
 *   array. If it was ``truncated``, the picker re-queries with the DEBOUNCED
 *   (300 ms) search text on each keystroke and renders results
 *   SERVER-AUTHORITATIVELY — in the returned order, with NO client-side
 *   filtering. See {@link useServerOptions}. Either way a loading hint shows
 *   while a search is in flight without blanking the previously-rendered
 *   options (``keepPreviousData``).
 *
 * Rows are listed as "Not set" (with ``notSet``), then the selected values,
 * then the source's other options, then — always last — the typed text as a
 * new value (with ``customValue``). The selected values are matched by the
 * search like any row, and listed whether or not the source still returns
 * them, so each carries its own label (see ``OptionList``). They are ranked
 * by popularity where the source counts its options (``counts``), else in the
 * source's order; each counted row shows its count.
 *
 * Both modes report selection changes through ``onChange`` as labeled
 * options, so every selected value carries its own display label.
 *
 * A click toggles one row; a shift+click extends from the last row clicked,
 * giving every row between them the state the clicked row toggles to (see
 * ``OptionListbox``). Beside the search box, Clear all unticks every row at
 * once, "Not set" included; with nothing ticked, Select all ticks every row the
 * search lists instead.
 */
export function OptionsValueInput({
  comparator,
  value,
  onChange,
  options,
  optionsKey,
  size = "md",
  invalid = false,
  notSet,
  memberLabels,
  hasUnset,
  counts,
  unsetCount,
  minSearchLength = MIN_SERVER_SEARCH_LENGTH,
  autoFocus = false,
  customValue,
  onDraftValidChange,
}: OptionsValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  // The input stays immediate; the server path searches the settled term.
  // Trimmed so a trailing space can't turn an exact match into `%acme %`.
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const debounceSearch = useDebouncedCallback(
    setDebouncedSearch,
    SEARCH_DEBOUNCE_MS,
  );
  // Threaded down to whichever list variant is active, all the way to the
  // `role="listbox"` element itself, so ArrowDown can focus it directly
  // instead of guessing at its DOM position via `querySelector`.
  const listboxRef = React.useRef<HTMLDivElement>(null);

  if (isValueless(comparator)) {
    return null;
  }

  const handleSearchChange = (next: string) => {
    setSearchValue(next);
    debounceSearch(next.trim());
  };

  const selection: SelectionProps = {
    value,
    onChange,
    notSet,
    memberLabels,
    customValue,
    onDraftValidChange,
  };

  return (
    <SearchFieldContext.Provider
      value={{
        size,
        invalid,
        autoFocus,
        takesValues: customValue !== undefined,
      }}
    >
      <div className={styles.input}>
        {typeof options === "function" ? (
          <ServerOptionList
            resolver={options}
            // Non-null by construction: the props union requires `optionsKey`
            // exactly when `options` is a resolver, which this branch established.
            optionsKey={optionsKey!}
            search={debouncedSearch}
            searchValue={searchValue}
            minSearchLength={minSearchLength}
            onSearchChange={handleSearchChange}
            selection={selection}
            listboxRef={listboxRef}
          />
        ) : (
          <StaticOptionList
            options={options}
            hasUnset={hasUnset}
            counts={counts}
            unsetCount={unsetCount}
            searchValue={searchValue}
            onSearchChange={handleSearchChange}
            selection={selection}
            listboxRef={listboxRef}
          />
        )}
      </div>
    </SearchFieldContext.Provider>
  );
}

/**
 * What a list variant feeds the shared ``OptionList``: the current result set
 * (in render order) plus its state flags. Splitting by SOURCE — rather than
 * having each variant assemble its own rows — is what guarantees pinning and
 * the loading/error/empty/truncation branches exist on both paths.
 */
interface OptionSource {
  options: LabeledOption[];
  isLoading: boolean;
  isError: boolean;
  truncated: boolean;
  /** See `ResolvedOptions.hasUnset`; absent when the source can't tell. */
  hasUnset?: boolean;
  /** See `ResolvedOptions.counts`. */
  counts?: Readonly<Record<string, number>>;
  /** See `ResolvedOptions.unsetCount`. */
  unsetCount?: number;
}

/**
 * Static, in-memory option source — filtered CLIENT-SIDE by label substring,
 * synchronously (which is why the array isn't just wrapped in a resolver).
 * Search matches ``label`` (what the user sees), never the opaque wire
 * ``value``. Consumers must keep the set small enough to hold in memory.
 */
function useStaticOptions(
  options: string[] | LabeledOption[],
  searchValue: string,
  hasUnset: boolean | undefined,
  counts: Readonly<Record<string, number>> | undefined,
  unsetCount: number | undefined,
): OptionSource {
  const normalized = React.useMemo(
    () => (options as FilterOption[]).map(normalizeOption),
    [options],
  );
  const filtered = React.useMemo(
    () => filterByLabel(normalized, searchValue),
    [normalized, searchValue],
  );

  return React.useMemo(
    () => ({
      options: filtered,
      isLoading: false,
      isError: false,
      truncated: false,
      hasUnset,
      counts,
      unsetCount,
    }),
    [filtered, hasUnset, counts, unsetCount],
  );
}

/**
 * Server-searched option source, which downgrades to client-side filtering as
 * soon as it can prove that's equivalent.
 *
 * On open it queries the resolver with the empty search. What comes back
 * decides the mode for the life of the mount — pointing the picker at a
 * different source means remounting it (see ``OptionsSourceProps``), which
 * re-runs the decision from scratch:
 *
 * - **Complete** (``truncated`` falsy) — the server returned the ENTIRE option
 *   set, so no later search can surface an option this page doesn't already
 *   hold. The set is latched and every subsequent keystroke filters it in
 *   memory: no debounce wait, no request per keystroke, no loading flicker.
 * - **Truncated** — the server capped the page, so options exist that aren't on
 *   screen and only the server can find them. The picker stays server-searched:
 *   each debounced keystroke re-queries, results render verbatim with NO
 *   client-side filtering, and a hint tells the user to narrow.
 *
 * In both modes ``keepPreviousData`` holds the previous page while a request is
 * in flight, so the list never blanks mid-keystroke.
 */
function useServerOptions(
  resolver: FilterOptionsResolver,
  optionsKey: string,
  search: string,
  searchValue: string,
  minSearchLength: number,
): OptionSource {
  // Latched to the full option set (and what it says about unset values and
  // popularity) once the empty search proves the resolver returned everything;
  // `null` while the picker is still server-searched.
  const [completeSet, setCompleteSet] = React.useState<Omit<
    ResolvedOptions,
    "truncated"
  > | null>(null);

  const isComplete = completeSet !== null;

  // With the whole set in hand there is nothing left to ask the server, so the
  // query stays pinned to the empty search and never refetches on a keystroke.
  const activeSearch = isComplete ? "" : search;

  // Per-mount id folded into the query key so two concurrently-rendered
  // pickers with the same `optionsKey` (e.g. a "tags" picker each on
  // DatasetsList, EventsList, and FilesList — three different resolvers,
  // potentially three different orgs) never share a query. Without it they
  // collide on `["filter-options", optionsKey, activeSearch]`, and TanStack
  // treats the second mount as an additional observer of the FIRST mount's
  // query — the second picker would render the first resolver's results, a
  // cross-resolver (and potentially cross-org) data leak. `gcTime: 0` below
  // already means nothing outlives this mount, so the extra key segment
  // costs nothing.
  const instanceId = React.useId();

  const query = useQuery({
    // Named by which source is active plus the search. Resolver IDENTITY is
    // deliberately absent: `optionsKey` is the caller's declaration of which
    // source this is, so a resolver rebuilt every render (an inline arrow, say)
    // is harmless rather than a refetch per render.
    //
    // `optionsKey` is not globally unique (it is the field name for
    // `set`/`enum`, so "tags" collides across the datasets and files pages).
    // The per-mount `instanceId` keeps concurrent mounts apart, and nothing is
    // retained across mounts — see the gcTime/staleTime note below.
    queryKey: ["filter-options", instanceId, optionsKey, activeSearch],
    queryFn: ({ signal }) => resolver(activeSearch, signal),
    // Retention is deliberately off: it belongs to the resolver, which is the
    // only layer that can see the endpoint and org behind it. Resolvers wanting
    // cross-mount sharing cache internally against the app query client with a
    // fully-qualified key — see `useAuditIdentityOptions`.
    gcTime: 0,
    staleTime: 0,
    // Hold back terms too short to search efficiently server-side; the
    // previous page stays visible via `placeholderData` until the term grows.
    enabled:
      activeSearch.length === 0 || activeSearch.length >= minSearchLength,
    // Keep the last page visible while the next search is in flight so the list
    // doesn't blank on every keystroke.
    placeholderData: keepPreviousData,
  });

  // A resolver may return a bare list (complete, no truncation) or a
  // `{ options, truncated }` object; normalize to the object shape. Memoized
  // so the no-data fallback keeps a stable identity — `options` (and the
  // pinning effect keyed on it) must only change when the RESULTS change.
  const resolved: ResolvedOptions = React.useMemo(() => {
    if (Array.isArray(query.data)) {
      return { options: query.data, truncated: false };
    }
    return query.data ?? { options: [], truncated: false };
  }, [query.data]);

  // Latch the complete set the first time the EMPTY search returns untruncated.
  // The `isPlaceholderData` guard matters: with `keepPreviousData`, returning to
  // the empty search briefly reports the previous search's (narrower) page under
  // the empty key, and latching that would freeze the picker on a subset.
  const isCompletePage =
    query.isSuccess && !query.isPlaceholderData && !resolved.truncated;
  React.useEffect(() => {
    if (activeSearch === "" && isCompletePage) {
      setCompleteSet({
        options: resolved.options,
        hasUnset: resolved.hasUnset,
        counts: resolved.counts,
        unsetCount: resolved.unsetCount,
      });
    }
  }, [activeSearch, isCompletePage, resolved]);

  // Snapshot of the most recent SUCCESSFUL response, held in a ref rather
  // than relied upon from TanStack's own cache: each debounced search term
  // is its own queryKey (see above), so a search term that has just failed
  // is a query that has NEVER had data — there is nothing for TanStack to
  // fall back on. `placeholderData: keepPreviousData` only substitutes data
  // while the query is `pending`; once it settles into `error`, TanStack
  // stops applying it, and `query.data` reverts to `undefined`. Updating
  // this ref during render (mirroring `valueRef` below) is what actually
  // keeps the last good page on screen through that transition.
  const lastGoodRef = React.useRef<ResolvedOptions>({
    options: [],
    truncated: false,
  });
  if (query.data !== undefined) {
    lastGoodRef.current = resolved;
  }
  const effectiveResolved = query.isError ? lastGoodRef.current : resolved;

  const normalized = React.useMemo(
    () =>
      (
        (completeSet?.options ?? effectiveResolved.options) as FilterOption[]
      ).map(normalizeOption),
    [completeSet, effectiveResolved.options],
  );

  // In complete mode, filter the latched set by label substring — the same
  // client-side match `useStaticOptions` applies. Driven by the RAW search text
  // rather than the debounced copy: there is no request to collapse, so waiting
  // out the debounce would only add lag.
  const visible = React.useMemo(
    () => (isComplete ? filterByLabel(normalized, searchValue) : normalized),
    [isComplete, normalized, searchValue],
  );

  // In complete mode the query is parked on the empty search and any refetch is
  // a background revalidation of a set already on screen — surfacing a spinner
  // for that would flicker mid-typing, when nothing is actually being fetched
  // for the user's keystroke.
  const isLoading = query.isFetching && !isComplete;
  const { hasUnset, counts, unsetCount } = completeSet ?? effectiveResolved;

  return React.useMemo(
    () => ({
      options: visible,
      isLoading,
      isError: query.isError,
      // Exclusive with the latch by construction: a complete set is by
      // definition untruncated, so this can only be true while server-searched.
      truncated: effectiveResolved.truncated ?? false,
      hasUnset,
      counts,
      unsetCount,
    }),
    [
      visible,
      isLoading,
      query.isError,
      effectiveResolved.truncated,
      hasUnset,
      counts,
      unsetCount,
    ],
  );
}

/** Client-side substring match on ``label`` — what the user sees, never the wire value. */
function filterByLabel(
  options: LabeledOption[],
  searchValue: string,
): LabeledOption[] {
  if (searchValue.trim() === "") {
    return options;
  }
  return options.filter((option) => matchesLabel(option.label, searchValue));
}

/** Whether `label` contains the search text, ignoring case and padding. */
function matchesLabel(label: string, searchValue: string): boolean {
  return label.toLowerCase().includes(searchValue.trim().toLowerCase());
}

/** What is picked and how picking changes it, threaded down to the rows. */
interface SelectionProps {
  value: LabeledOption[];
  onChange: (next: LabeledOption[], notSet?: boolean) => void;
  notSet: NotSetRow | undefined;
  memberLabels: ListMemberLabels | undefined;
  customValue: CustomValueOptions | undefined;
  onDraftValidChange: ((valid: boolean) => void) | undefined;
}

/**
 * One row of the listbox: an option, the "Not set" member, or the typed text
 * offered as a new value. The last two are kept apart from the options so no
 * option can be mistaken for them.
 */
type ListRow =
  | { kind: "option"; option: LabeledOption }
  | { kind: "not-set"; notSet: NotSetRow }
  | { kind: "custom"; value: string };

/**
 * A row's identity, stable across reorders: the active row and React's keys
 * follow it. Option values can't collide with the other two, since no option's
 * key but an option's has the prefix.
 */
function rowKey(row: ListRow): string {
  switch (row.kind) {
    case "not-set":
      return "not-set";
    case "custom":
      return "custom";
    case "option":
      return `option:${row.option.value}`;
  }
}

/** What the picker has learned of the source's values over its mount. */
interface SeenOptions {
  /** Each value's latest reported popularity. */
  counts: Map<string, number>;
  /** Where the source first listed each value, counting from 0. */
  order: Map<string, number>;
  /** The latest reported count of unset entities. */
  unsetCount: number | undefined;
}

/**
 * Gathers what every page the source returns says about its values, across
 * searches. A selected value keeps its count and rank after a search stops
 * returning it, so its row neither loses its count nor moves.
 */
function useSeenOptions(source: OptionSource): SeenOptions {
  const seenRef = React.useRef<SeenOptions>({
    counts: new Map(),
    order: new Map(),
    unsetCount: undefined,
  });
  // Recorded during render, like `valueRef` in `OptionList`: recording the
  // same page twice changes nothing.
  const seen = seenRef.current;
  for (const [value, count] of Object.entries(source.counts ?? {})) {
    seen.counts.set(value, count);
  }
  for (const option of source.options) {
    if (!seen.order.has(option.value)) {
      seen.order.set(option.value, seen.order.size);
    }
  }
  if (source.unsetCount !== undefined) {
    seen.unsetCount = source.unsetCount;
  }
  return seen;
}

/**
 * The order of the selected block, for a stable sort: most popular first,
 * then the values with no count; ties keep the source's order, and values it
 * never listed (typed, or set from outside) go last, in the order they were
 * picked.
 */
function compareSelected(
  a: LabeledOption,
  b: LabeledOption,
  seen: SeenOptions,
): number {
  const negated = (count: number | undefined) =>
    count === undefined ? undefined : -count;
  return (
    compareMissingLast(
      negated(seen.counts.get(a.value)),
      negated(seen.counts.get(b.value)),
    ) || compareMissingLast(seen.order.get(a.value), seen.order.get(b.value))
  );
}

/** Ascending, with `undefined` after every number. */
function compareMissingLast(
  x: number | undefined,
  y: number | undefined,
): number {
  if (x === undefined || y === undefined) {
    return Number(x === undefined) - Number(y === undefined);
  }
  return x - y;
}

interface OptionListProps {
  source: OptionSource;
  searchValue: string;
  onSearchChange: (next: string) => void;
  selection: SelectionProps;
  listboxRef: React.RefObject<HTMLDivElement>;
}

/**
 * Shared row assembly and state branches for BOTH option sources: lists the
 * selected values first, takes typed values (``customValue``), renders the
 * loading / error / empty / truncation states, and delegates row rendering to
 * ``OptionListbox``.
 *
 * Owns the search input too, so the in-flight indicator can live inside it
 * rather than as a block that reflows the list (see ``SearchInput``).
 */
function OptionList({
  source,
  searchValue,
  onSearchChange,
  selection,
  listboxRef,
}: OptionListProps) {
  const {
    value,
    onChange,
    notSet,
    memberLabels,
    customValue,
    onDraftValidChange,
  } = selection;
  // The selected values lead the list, each with the label it was picked
  // under, so no lookup is needed and a value the source no longer returns
  // stays visible and deselectable.
  //
  // Which values lead is a SNAPSHOT taken when the result set changes, not a
  // per-render derivation from the live selection. Deriving it live would move
  // a row to the top the instant it is ticked, and unmount a leading row the
  // instant it is unticked — dropping keyboard focus to <body> and leaving no
  // way to undo the click short of retyping the search that surfaced the row.
  // A snapshot keeps every row where it is until the next search moves the
  // results on.
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const [leading, setLeading] = React.useState<LabeledOption[]>(() => value);
  // NUL-joined and keyed on result CONTENT, not array identity: a parent re-rendering with a
  // freshly-built (but equal) options array must not re-take the snapshot
  // mid-toggle, or it degenerates to the live derivation it exists to avoid.
  const resultsKey = React.useMemo(
    () => source.options.map((option) => option.value).join("\u0000"),
    [source.options],
  );
  React.useEffect(() => {
    setLeading(valueRef.current);
  }, [resultsKey]);

  const trimmed = searchValue.trim();
  // The typed text as the value it stands for, when the picker takes typed
  // values and the field can hold it. Only the search is trimmed: the value is
  // read from the text as typed (see `CustomValueOptions`).
  const draft =
    customValue !== undefined && trimmed !== ""
      ? customValue.normalize(searchValue)
      : undefined;
  const draftInvalid =
    customValue !== undefined && trimmed !== "" && draft === undefined;

  // The snapshot, plus any selected value it doesn't hold that isn't shown
  // elsewhere either (one just typed, or set from outside the picker) — a
  // selection is never left off the list. Matched by the search like any row,
  // or by naming its value exactly ("3.0" for a selected 3). Ordered as the
  // source would rank them (see `compareSelected`), never by when each was
  // picked; a toggle changes no row's rank, so rows still stay put.
  const seen = useSeenOptions(source);
  const resultValues = new Set(source.options.map((option) => option.value));
  const leadingValues = new Set(leading.map((option) => option.value));
  const selectedRows = [
    ...leading,
    ...value.filter(
      (option) =>
        !leadingValues.has(option.value) && !resultValues.has(option.value),
    ),
  ]
    .filter(
      (option) =>
        matchesLabel(option.label, searchValue) || option.value === draft,
    )
    .sort((a, b) => compareSelected(a, b, seen));
  const selectedRowValues = new Set(selectedRows.map((option) => option.value));
  const results = source.options.filter(
    (option) => !selectedRowValues.has(option.value),
  );

  // "Not set" leads, matched by its name or by what it is drawn as ("is not
  // set"). A source that reports every entity set withholds it, unless it's
  // already part of the selection and so must stay listed to be removable.
  const notSetNames =
    memberLabels === undefined
      ? [NOT_SET_LABEL]
      : [NOT_SET_LABEL, memberLabels.notSet];
  const listsNotSet =
    notSet !== undefined &&
    notSetNames.some((name) => matchesLabel(name, searchValue)) &&
    (notSet.selected || source.hasUnset !== false);
  // Typed text that names the "Not set" row exactly means that row: it is
  // added as "Not set", not as a value spelled the same, and gets no "Use"
  // row of its own. An option holding those very words is more specific, so
  // it wins (see `addDraft`), and "Not set" stays a click away.
  const searchNamesNotSet =
    listsNotSet &&
    notSetNames.some((name) => name.toLowerCase() === trimmed.toLowerCase());

  const isValue = (candidate: string) =>
    value.some((option) => option.value === candidate) ||
    selectedRowValues.has(candidate) ||
    resultValues.has(candidate);
  const offersDraft =
    draft !== undefined && !isValue(draft) && !searchNamesNotSet;
  // The typed text, which no listed member holds, always comes last: everything
  // above it is a member the author can tick, and it is the way out when none
  // of them is the one.
  const rows: ListRow[] = [
    ...(listsNotSet ? [{ kind: "not-set" as const, notSet }] : []),
    ...selectedRows.map((option) => ({ kind: "option" as const, option })),
    ...results.map((option) => ({ kind: "option" as const, option })),
    ...(offersDraft ? [{ kind: "custom" as const, value: draft }] : []),
  ];

  // What the screen reader hears after the typed text is added.
  const [announcement, setAnnouncement] = React.useState("");
  const changeSearch = (next: string) => {
    setAnnouncement("");
    onSearchChange(next);
  };

  /**
   * Adds the typed text as a value: the listed option it names exactly, if
   * there is one, so the option keeps its label; else "Not set", if the text
   * names that row. Reports whether the text named a value at all.
   *
   * Clears the search, for the next value, unless `keepSearch`: focus leaving
   * the picker adds the text on its way out, and clearing the search then
   * would bring back every row, growing the picker and moving whatever is
   * being clicked (a form's submit button) out from under the pointer before
   * the click lands. Kept, the list stays one row: the added value, ticked.
   */
  const addDraft = ({ keepSearch = false } = {}): boolean => {
    if (draft === undefined) {
      return false;
    }
    if (!keepSearch) {
      onSearchChange("");
    }
    if (value.some((option) => option.value === draft)) {
      setAnnouncement(`${draft} is already added`);
      return true;
    }
    const listed = [...selectedRows, ...results].find(
      (option) => option.value === draft || option.label === searchValue,
    );
    if (listed === undefined && searchNamesNotSet) {
      if (notSet.selected) {
        setAnnouncement(`${NOT_SET_LABEL} is already added`);
      } else {
        notSet.onToggle();
        setAnnouncement(`Added ${NOT_SET_LABEL}`);
      }
      return true;
    }
    const added = listed ?? { label: draft, value: draft };
    onChange([...value, added]);
    setAnnouncement(`Added ${added.label}`);
    return true;
  };

  // A form can accept typed text as a value: it's added when focus leaves.
  const draftValid = draft !== undefined;
  const onDraftValidChangeRef = React.useRef(onDraftValidChange);
  onDraftValidChangeRef.current = onDraftValidChange;
  React.useEffect(() => {
    if (customValue === undefined) {
      return;
    }
    onDraftValidChangeRef.current?.(draftValid);
    // The draft leaves with the input (an operator without a list, a closed
    // form), so the form must stop counting it.
    return () => onDraftValidChangeRef.current?.(false);
  }, [customValue, draftValid]);

  // Pressing on a row (or the scrollbar, or either clear button) moves focus
  // within the picker, which is no reason to add the typed text. The browser
  // doesn't always say where focus is going (Safari doesn't focus a pressed
  // button), so a press inside is noted as it starts, before the blur.
  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const pressingInsideRef = React.useRef(false);
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget;
    const staysInside =
      pressingInsideRef.current ||
      (next instanceof Node && containerRef.current?.contains(next) === true);
    if (!staysInside) {
      addDraft({ keepSearch: true });
    }
  };

  const handleEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // With nothing typed, Enter is the surrounding form's, to submit.
    if (customValue === undefined || trimmed === "") {
      return;
    }
    event.preventDefault();
    addDraft();
  };

  // Narrowing a long selection to one value is otherwise a click per row.
  // Each button changes the values and "Not set" in one call (see `onChange`).
  const hasSelection = value.length > 0 || notSet?.selected === true;
  const clearSelection = () => {
    if (notSet?.selected) {
      onChange([], false);
    } else {
      onChange([]);
    }
  };
  // Every row the search lists, "Not set" included; not the typed text, which
  // is a value to add rather than one listed. Only of a loaded page, where the
  // source has more (`truncated`).
  const listedOptions = rows.flatMap((row) =>
    row.kind === "option" ? [row.option] : [],
  );
  const canSelectAll = listedOptions.length > 0 || listsNotSet;
  const selectAll = () => {
    if (listsNotSet && !notSet.selected) {
      onChange(listedOptions, true);
    } else {
      onChange(listedOptions);
    }
  };

  const optionCount = selectedRows.length + results.length;
  const isSettled = !source.isLoading && !source.isError;

  return (
    <div
      ref={containerRef}
      className={styles.picker}
      onPointerDownCapture={() => {
        pressingInsideRef.current = true;
        window.setTimeout(() => {
          pressingInsideRef.current = false;
        }, 0);
      }}
    >
      <SearchInput
        inputRef={searchInputRef}
        value={searchValue}
        onChange={changeSearch}
        isLoading={source.isLoading}
        listboxRef={listboxRef}
        draftInvalid={draftInvalid}
        announcement={announcement}
        onEnter={handleEnter}
        onBlur={customValue === undefined ? undefined : handleBlur}
        selectionAction={
          hasSelection
            ? { label: "Clear all", onClick: clearSelection, disabled: false }
            : {
                label: "Select all",
                onClick: selectAll,
                disabled: !canSelectAll,
              }
        }
      />
      {source.isError && <ErrorState />}
      {draftInvalid && (
        <div className={styles.status} role="status" aria-live="polite">
          Not a valid value
        </div>
      )}
      {rows.length > 0 && (
        <OptionListbox
          rows={rows}
          value={value}
          onChange={onChange}
          onPressCustom={addDraft}
          onExitTop={() => searchInputRef.current?.focus()}
          counts={seen.counts}
          unsetCount={seen.unsetCount}
          memberLabels={memberLabels}
          listboxRef={listboxRef}
          activeResetKey={searchValue}
          truncated={source.truncated && !source.isError}
          resultCount={source.options.length}
        />
      )}
      {customValue === undefined
        ? rows.length === 0 && isSettled && <EmptyState />
        : optionCount === 0 &&
          trimmed === "" &&
          isSettled && (
            <div className={styles.status} role="status" aria-live="polite">
              Type a value to add it
            </div>
          )}
    </div>
  );
}

/** The search box's setup, set once by `OptionsValueInput`. */
const SearchFieldContext = React.createContext<{
  size: TextInputSize;
  invalid: boolean;
  autoFocus: boolean;
  /** Whether typed text can become a value (`customValue`). */
  takesValues: boolean;
}>({ size: "md", invalid: false, autoFocus: false, takesValues: false });

function SearchInput({
  inputRef,
  value,
  onChange,
  isLoading,
  listboxRef,
  draftInvalid,
  announcement,
  onEnter,
  onBlur,
  selectionAction,
}: {
  /** The search input, which the listbox hands focus back to (ArrowUp from its top). */
  inputRef: React.RefObject<HTMLInputElement>;
  value: string;
  onChange: (next: string) => void;
  isLoading: boolean;
  listboxRef: React.RefObject<HTMLDivElement>;
  /** The typed text names no value the field can hold. */
  draftInvalid: boolean;
  /** News for screen readers, such as a value just added. */
  announcement: string;
  onEnter: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur: ((event: React.FocusEvent<HTMLInputElement>) => void) | undefined;
  /** The button beside the search box: Clear all while anything is ticked,
   *  else Select all. */
  selectionAction: {
    label: (typeof SELECTION_ACTION_LABELS)[number];
    onClick: () => void;
    disabled: boolean;
  };
}) {
  const { size, invalid, autoFocus, takesValues } =
    React.useContext(SearchFieldContext);
  let adornment: React.ReactNode = null;
  if (isLoading) {
    adornment = <Spinner aria-hidden />;
  } else if (value.length > 0) {
    adornment = (
      <button
        type="button"
        className={styles.searchClear}
        onClick={() => onChange("")}
        aria-label="Clear search"
      >
        <XIcon size={14} />
      </button>
    );
  }

  return (
    <>
      <div className={styles.searchRow}>
        <div className={styles.search}>
          <TextInput
            ref={inputRef}
            size={size}
            autoComplete="off"
            type="search"
            autoFocus={autoFocus}
            // `undefined` rather than `false`: React renders the latter as the
            // string "false", which is still an `aria-invalid` attribute.
            aria-invalid={invalid || draftInvalid || undefined}
            className={styles.searchInput}
            placeholder={
              takesValues ? "Search or add values…" : "Search options..."
            }
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onBlur={onBlur}
            aria-label="Search options"
            // The combobox affordance: ArrowDown hands focus to the option
            // listbox (a sibling rendered by whichever list variant is active),
            // whose own keyboard model takes over from there.
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                listboxRef.current?.focus();
              } else if (event.key === "Enter") {
                onEnter(event);
              }
            }}
          />
          {adornment !== null && (
            <span className={styles.searchAdornment}>{adornment}</span>
          )}
        </div>
        <Button
          variant="text"
          size="small"
          color="inherit"
          className={styles.selectionAction}
          disabled={selectionAction.disabled}
          onClick={selectionAction.onClick}
        >
          {/* Both labels share one cell, so the button is as wide as the
              longer whichever shows, and the search box doesn't shift as the
              first row is ticked. Only the one showing names the button. */}
          <span className={styles.selectionActionLabels}>
            {SELECTION_ACTION_LABELS.map((label) =>
              label === selectionAction.label ? (
                <span key={label}>{label}</span>
              ) : (
                <span
                  key={label}
                  className={styles.selectionActionSpacer}
                  aria-hidden
                >
                  {label}
                </span>
              ),
            )}
          </span>
        </Button>
      </div>
      {/* The spinner is decorative; this carries the same news to screen
          readers, which the removed block-level loading row used to. */}
      <span className="sr-only" role="status" aria-live="polite">
        {isLoading ? "Loading options…" : announcement}
      </span>
    </>
  );
}

interface StaticOptionListProps {
  options: string[] | LabeledOption[];
  hasUnset: boolean | undefined;
  counts: Readonly<Record<string, number>> | undefined;
  unsetCount: number | undefined;
  searchValue: string;
  onSearchChange: (next: string) => void;
  selection: SelectionProps;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function StaticOptionList({
  options,
  hasUnset,
  counts,
  unsetCount,
  searchValue,
  onSearchChange,
  selection,
  listboxRef,
}: StaticOptionListProps) {
  const source = useStaticOptions(
    options,
    searchValue,
    hasUnset,
    counts,
    unsetCount,
  );
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      selection={selection}
      listboxRef={listboxRef}
    />
  );
}

interface ServerOptionListProps {
  resolver: FilterOptionsResolver;
  /** Names WHICH option source `resolver` currently is; keys its query. */
  optionsKey: string;
  /** Already debounced and trimmed by `OptionsValueInput`. */
  search: string;
  /** The immediate input text — `search` is its settled form. */
  searchValue: string;
  /** Shortest `search` sent to the resolver. */
  minSearchLength: number;
  onSearchChange: (next: string) => void;
  selection: SelectionProps;
  listboxRef: React.RefObject<HTMLDivElement>;
}

function ServerOptionList({
  resolver,
  optionsKey,
  search,
  searchValue,
  minSearchLength,
  onSearchChange,
  selection,
  listboxRef,
}: ServerOptionListProps) {
  const source = useServerOptions(
    resolver,
    optionsKey,
    search,
    searchValue,
    minSearchLength,
  );
  return (
    <OptionList
      source={source}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      selection={selection}
      listboxRef={listboxRef}
    />
  );
}

/**
 * The next selection after toggling ``option``: removed if already selected
 * (matched by wire ``value``), appended — label and all — if not.
 */
function toggleOption(
  current: LabeledOption[],
  option: LabeledOption,
): LabeledOption[] {
  return current.some((selected) => selected.value === option.value)
    ? current.filter((selected) => selected.value !== option.value)
    : [...current, option];
}

interface OptionListboxProps {
  rows: ListRow[];
  value: LabeledOption[];
  onChange: (next: LabeledOption[]) => void;
  /** Called when the typed-text row is pressed, to add it. */
  onPressCustom: () => void;
  /** Called on ArrowUp from the top row, to hand focus back to the search. */
  onExitTop: () => void;
  /** Each value's popularity, shown on its row. */
  counts: ReadonlyMap<string, number>;
  /** The "Not set" row's count. */
  unsetCount: number | undefined;
  /** The comparator pills rows are drawn with, if any. */
  memberLabels: ListMemberLabels | undefined;
  /** Threaded down from `OptionsValueInput` so ArrowDown in the search input
   * can focus this element directly (see the container's `onKeyDown`). */
  listboxRef: React.RefObject<HTMLDivElement>;
  /** The active row is cleared whenever this changes (the search text). */
  activeResetKey: string;
  /** Whether the server capped the result set. */
  truncated: boolean;
  /** Server rows only (pinned selections excluded) — what the cap applied to. */
  resultCount: number;
}

/**
 * Renders the option rows, virtualized: only the rows within (and just
 * outside) the scroll viewport are mounted, so a large option list stays cheap
 * to render, scroll, and interact with. The scroll container is a
 * ``role="listbox"`` div and each row a ``role="option"`` button, with a
 * ``role="presentation"`` sizer between them so the listbox still owns its
 * options in the accessibility tree; because most options are absent from the
 * DOM, each rendered option carries explicit ``aria-setsize`` /
 * ``aria-posinset`` so assistive tech still reports the true position and
 * total (which it would otherwise infer from sibling count).
 *
 * Keyboard model: the listbox itself is the single tab stop (row buttons are
 * ``tabIndex={-1}``). Arrow keys move an active row — reported through
 * ``aria-activedescendant``, scrolled into view via the virtualizer so it is
 * mounted before assistive tech looks it up — Home/End jump to the edges, and
 * Enter or Space toggles the active row. ArrowUp from the top row hands focus
 * back to the search input, the way ArrowDown from there entered the list.
 *
 * Range selection: a click (or Enter/Space) toggles one row and makes it the
 * anchor; a shift+click (or Shift+Enter/Space) extends from the anchor,
 * giving every row between the two, inclusive, the state the pressed row
 * toggles to. Ranges span the rows shown, pinned ones included. With no anchor
 * shown — nothing pressed yet, or the search moved it off the list — the
 * press toggles one row, as a plain one does.
 */
function OptionListbox({
  rows,
  value,
  onChange,
  onPressCustom,
  onExitTop,
  counts,
  unsetCount,
  memberLabels,
  listboxRef,
  activeResetKey,
  truncated,
  resultCount,
}: OptionListboxProps) {
  const baseId = React.useId();
  // The active row is held by its key, not its index: rows are added and
  // removed above it (a value set from outside, "Not set" once the source
  // reports on unset values), and an index would silently move the keyboard
  // to whatever row slid into its place.
  const [activeKey, setActiveKey] = React.useState<string | undefined>(
    undefined,
  );
  React.useEffect(() => {
    setActiveKey(undefined);
  }, [activeResetKey]);
  const keys = rows.map(rowKey);
  const active = activeKey === undefined ? -1 : keys.indexOf(activeKey);
  const selectedValues = React.useMemo(
    () => new Set(value.map((option) => option.value)),
    [value],
  );
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => listboxRef.current,
    estimateSize: () => OPTION_ROW_HEIGHT,
    overscan: OPTION_OVERSCAN,
  });

  const moveActive = (next: number) => {
    setActiveKey(keys[next]);
    virtualizer.scrollToIndex(next);
  };

  // Whether the keyboard is driving the list, which is when the active row is
  // drawn. A click makes its row the active one too, so the keyboard carries
  // on from it, but the pointer is what marks a clicked row: drawn as well, it
  // stayed lit after the pointer and focus had moved on.
  const [keyboardDriven, setKeyboardDriven] = React.useState(false);

  // The value of the option last pressed, where a range starts. Held as a
  // value rather than an index so a re-filtered list can tell whether it is
  // still shown.
  const anchorRef = React.useRef<string | undefined>(undefined);

  const pressRow = (row: ListRow, extend: boolean) => {
    if (row.kind === "not-set") {
      // "Not set" stays out of ranges: it neither ends nor anchors one. It is a
      // flag of the filter rather than one of its values, so a range of values
      // shouldn't sweep it in, and a click on it leaves the anchor on the last
      // value pressed.
      row.notSet.onToggle();
      return;
    }
    if (row.kind === "custom") {
      // Adding the typed text is an action, not a toggle of a listed value, so
      // it stays out of ranges too.
      onPressCustom();
      return;
    }
    const { option } = row;
    onChange(
      extend
        ? extendSelection({
            selected: value,
            visible: rows.flatMap((r) =>
              r.kind === "option" ? [r.option] : [],
            ),
            anchor: anchorRef.current,
            target: option,
            keyOf: (o) => o.value,
          })
        : toggleOption(value, option),
    );
    anchorRef.current = option.value;
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    setKeyboardDriven(true);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        moveActive(Math.min(active + 1, rows.length - 1));
        break;
      case "ArrowUp":
        event.preventDefault();
        if (active <= 0) {
          // Up past the top returns to the search, as ArrowDown left it, and
          // forgets the active row so coming back down starts at the top.
          setActiveKey(undefined);
          onExitTop();
        } else {
          moveActive(active - 1);
        }
        break;
      case "Home":
        event.preventDefault();
        moveActive(0);
        break;
      case "End":
        event.preventDefault();
        moveActive(rows.length - 1);
        break;
      case "Enter":
      case " ":
        if (active >= 0) {
          // preventDefault stops Space from scrolling the list and Enter
          // from submitting the enclosing filter form.
          event.preventDefault();
          pressRow(rows[active], event.shiftKey);
        }
        break;
    }
  };

  return (
    <div
      ref={listboxRef}
      className={styles.list}
      // The truncation hint is pinned over the bottom of the list, so it
      // takes a row of the minimum, a row being taller than it.
      style={{
        minHeight: `${
          (Math.min(rows.length, MIN_VISIBLE_ROWS) + (truncated ? 1 : 0)) *
          OPTION_ROW_HEIGHT
        }px`,
      }}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
      tabIndex={0}
      aria-activedescendant={
        active >= 0 ? `${baseId}-option-${active}` : undefined
      }
      data-keyboard={keyboardDriven || undefined}
      onKeyDown={handleKeyDown}
      onPointerDown={() => setKeyboardDriven(false)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setKeyboardDriven(false);
        }
      }}
      // React's onFocus fires for a focused descendant too, and pressing the mouse on a row
      // focuses that row. Answering with a scroll to the first row would pull the row out from
      // under the pointer before the mouse is released, and no click would reach any row; a
      // reader who has scrolled down finds that the first click on an option does nothing.
      // Only the listbox taking focus itself (Tab, or ArrowDown from the search input) seeds
      // an active row; a row click seeds it below.
      onFocus={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }
        setKeyboardDriven(true);
        if (active < 0 && rows.length > 0) {
          moveActive(0);
        }
      }}
    >
      <div
        className={styles.sizer}
        role="presentation"
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          let isSelected = false;
          let count: number | undefined;
          if (row.kind === "not-set") {
            isSelected = row.notSet.selected;
            count = unsetCount;
          } else if (row.kind === "option") {
            isSelected = selectedValues.has(row.option.value);
            count = counts.get(row.option.value);
          }
          const countId = `${baseId}-count-${virtualRow.index}`;
          return [
            <button
              key={keys[virtualRow.index]}
              id={`${baseId}-option-${virtualRow.index}`}
              type="button"
              role="option"
              tabIndex={-1}
              aria-selected={isSelected}
              aria-describedby={count === undefined ? undefined : countId}
              aria-setsize={rows.length}
              aria-posinset={virtualRow.index + 1}
              className={classNames(styles.row, {
                [styles.rowActive]: virtualRow.index === active,
              })}
              style={{
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
              // A shift-held mousedown extends the page's text selection by
              // default, which would highlight the labels a range sweeps over.
              onMouseDown={(event) => {
                if (event.shiftKey) {
                  event.preventDefault();
                }
              }}
              onClick={(event) => {
                setActiveKey(keys[virtualRow.index]);
                pressRow(row, event.shiftKey);
              }}
            >
              {row.kind === "custom" ? (
                // An addition, not a value to tick: a plus where the box goes.
                <span className={styles.customIcon} aria-hidden="true">
                  <PlusIcon />
                </span>
              ) : (
                // Drawn, not a control: the row is the option and carries
                // `aria-selected`, so a nested checkbox would only repeat it.
                <span
                  className={styles.checkbox}
                  data-checked={isSelected || undefined}
                  aria-hidden="true"
                >
                  {isSelected && <CheckIcon />}
                </span>
              )}
              <RowLabel row={row} memberLabels={memberLabels} />
              {count !== undefined && (
                // Hidden from the row's name, so the option is still named by
                // its label alone; the description below says what it is.
                <span className={styles.rowCount} aria-hidden="true">
                  {count.toLocaleString()}
                </span>
              )}
            </button>,
            count !== undefined && (
              // Beside the row rather than in it, so it isn't part of the
              // row's text; `hidden` keeps it out of the listbox's children.
              <span key={countId} id={countId} hidden>
                Popularity: {count.toLocaleString()}
              </span>
            ),
          ];
        })}
      </div>
      {/* Sticky to the bottom of the SCROLL viewport, and `role="presentation"`
          so a non-option node can't disturb the listbox's ownership of its
          options. */}
      {truncated && (
        <div className={styles.truncationHint} role="presentation">
          <span role="status" aria-live="polite">
            Showing the first {resultCount} matches — refine your search to
            narrow.
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * A row's text: the option's label, "Not set", or the typed text to add. As a
 * list member, the label follows its comparator pill, and "Not set" is its pill
 * alone.
 */
function RowLabel({
  row,
  memberLabels,
}: {
  row: ListRow;
  memberLabels: ListMemberLabels | undefined;
}) {
  if (row.kind === "custom") {
    return <span className={styles.rowLabel}>Use “{row.value}”</span>;
  }
  if (memberLabels === undefined) {
    return row.kind === "not-set" ? (
      <span className={classNames(styles.rowLabel, styles.rowLabelNotSet)}>
        {NOT_SET_LABEL}
      </span>
    ) : (
      <span className={styles.rowLabel}>{row.option.label}</span>
    );
  }
  if (row.kind === "not-set") {
    return <ListMember comparator={memberLabels.notSet} />;
  }
  // Siblings in the row rather than a `ListMember`, so the label alone
  // truncates while the pill keeps its width.
  return (
    <>
      <ComparatorPill label={memberLabels.value} decorative />{" "}
      <span className={styles.rowLabel}>{row.option.label}</span>
    </>
  );
}

function EmptyState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      No matching options
    </div>
  );
}

function ErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
